import { Alert, Button, Card, DatePicker, Descriptions, Form, Input, Select, Space, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationCampusSelect,
  EducationGradeSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';
import type { EnquiryRow } from '@/pages/education/EnquiryListPage';

type ApiError = { response?: { data?: { message?: string } } };

type CreateValues = {
  contact_name: string;
  phone?: string;
  email?: string;
  grade_interest?: string;
  preferred_session_label?: string;
  source_channel?: string;
  follow_up_on?: dayjs.Dayjs | null;
  notes?: string;
};

type UpdateValues = {
  status: string;
  follow_up_on?: dayjs.Dayjs | null;
  notes?: string;
};

type ConvertValues = {
  first_name: string;
  last_name?: string;
  date_of_birth?: dayjs.Dayjs | null;
  campus_id: string;
  session_id: string;
  grade_level_id: string;
};

/** Full-page enquiry create (route /new) and enquiry detail with follow-up and convert-to-application (route /:id). */
export default function EnquiryFormPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = Boolean(useAuthStore((state) => state.user?.permissions?.includes('education.admission.manage')));
  const [createForm] = Form.useForm<CreateValues>();
  const [updateForm] = Form.useForm<UpdateValues>();
  const [convertForm] = Form.useForm<ConvertValues>();
  const fail = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));

  const detail = useQuery({
    queryKey: ['education-enquiry', id],
    enabled: !isNew,
    queryFn: () => api.get<EnquiryRow>(`/education/admissions/enquiries/${id}`),
  });
  const enquiry = detail.data?.data;
  const converted = enquiry?.status === 'converted' || Boolean(enquiry?.converted_application_id);

  useEffect(() => {
    if (!enquiry) return;
    updateForm.setFieldsValue({
      status: enquiry.status && enquiry.status !== 'converted' ? enquiry.status : 'open',
      follow_up_on: enquiry.follow_up_on ? dayjs(String(enquiry.follow_up_on).slice(0, 10)) : null,
      notes: enquiry.notes || undefined,
    });
  }, [enquiry, updateForm]);

  const create = useMutation({
    mutationFn: (values: CreateValues) =>
      api.post<{ id: string }>('/education/admissions/enquiries', {
        ...values,
        follow_up_on: values.follow_up_on ? values.follow_up_on.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: (res) => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-enquiries'] });
      navigate(res.data?.id ? `/education/admissions/enquiries/${res.data.id}` : '/education/admissions/enquiries');
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: (values: UpdateValues) =>
      api.put(`/education/admissions/enquiries/${id}`, {
        status: values.status,
        follow_up_on: values.follow_up_on ? values.follow_up_on.format('YYYY-MM-DD') : null,
        notes: values.notes ?? '',
      }),
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-enquiries'] });
      queryClient.invalidateQueries({ queryKey: ['education-enquiry', id] });
    },
    onError: fail,
  });

  const convert = useMutation({
    mutationFn: (values: ConvertValues) =>
      api.post<{ application_id: string }>(`/education/admissions/enquiries/${id}/convert`, {
        ...values,
        date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: (res) => {
      message.success(t('enquiryConverted', { defaultValue: 'Enquiry converted to a draft application' }));
      queryClient.invalidateQueries({ queryKey: ['education-enquiries'] });
      queryClient.invalidateQueries({ queryKey: ['education-enquiry', id] });
      navigate(res.data?.application_id ? `/education/admissions/${res.data.application_id}` : '/education/admissions');
    },
    onError: fail,
  });

  const back = <Button onClick={() => navigate('/education/admissions/enquiries')}>{t('enquiriesTitle', { defaultValue: 'Admission enquiries' })}</Button>;

  if (isNew) {
    return (
      <EducationPageChrome title={t('newEnquiry', { defaultValue: 'New enquiry' })} extra={back}>
        {!canManage ? <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} style={{ marginBottom: 16 }} /> : null}
        <Card size="small" style={{ maxWidth: 640 }}>
          <Form form={createForm} layout="vertical" disabled={!canManage} onFinish={(values) => create.mutate(values)}>
            <Form.Item name="contact_name" label={t('guardianName')} rules={[{ required: true }]}>
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="phone" label={t('phone')}>
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item name="email" label={t('email')}>
              <Input type="email" maxLength={200} />
            </Form.Item>
            <Form.Item name="grade_interest" label={t('grade')}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="preferred_session_label" label={t('session')}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="source_channel" label={t('source', { defaultValue: 'Source' })}>
              <Input maxLength={50} placeholder="walk-in, website, referral" />
            </Form.Item>
            <Form.Item name="follow_up_on" label={t('followUpOn', { defaultValue: 'Follow-up' })}>
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
            <Form.Item name="notes" label={t('notes', { defaultValue: 'Notes' })}>
              <Input.TextArea rows={3} maxLength={500} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={create.isPending}>
              {t('save')}
            </Button>
          </Form>
        </Card>
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={enquiry?.contact_name || t('enquiriesTitle', { defaultValue: 'Admission enquiries' })}
      extra={back}
      tags={enquiry ? <Tag color={converted ? 'green' : 'gold'}>{enquiry.status || 'open'}</Tag> : null}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {detail.isError ? <Alert type="error" showIcon message={t('unavailable', { status: '' })} /> : null}
        <Card size="small" loading={detail.isLoading}>
          <Descriptions size="small" column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label={t('phone')}>{enquiry?.phone || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('email')}>{enquiry?.email || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('grade')}>{enquiry?.grade_interest || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('session')}>{enquiry?.preferred_session_label || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('source', { defaultValue: 'Source' })}>{enquiry?.source_channel || '—'}</Descriptions.Item>
          </Descriptions>
        </Card>

        {converted ? (
          <Alert
            type="success"
            showIcon
            message={t('enquiryAlreadyConverted', { defaultValue: 'This enquiry was converted to an application.' })}
            action={
              enquiry?.converted_application_id ? (
                <Button size="small" onClick={() => navigate(`/education/admissions/${enquiry.converted_application_id}`)}>
                  {t('open')}
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <Card size="small" title={t('enquiryFollowUp', { defaultValue: 'Follow-up' })} style={{ maxWidth: 640 }}>
              <Form form={updateForm} layout="vertical" disabled={!canManage} onFinish={(values) => update.mutate(values)}>
                <Form.Item name="status" label={t('status')} rules={[{ required: true }]}>
                  <Select options={['open', 'contacted', 'closed'].map((value) => ({ value, label: value }))} />
                </Form.Item>
                <Form.Item name="follow_up_on" label={t('followUpOn', { defaultValue: 'Follow-up' })}>
                  <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                </Form.Item>
                <Form.Item name="notes" label={t('notes', { defaultValue: 'Notes' })}>
                  <Input.TextArea rows={3} maxLength={500} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={update.isPending}>
                  {t('save')}
                </Button>
              </Form>
            </Card>

            {canManage ? (
              <Card size="small" title={t('convertEnquiry', { defaultValue: 'Convert to application' })} style={{ maxWidth: 640 }}>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={t('convertEnquiryHint', {
                    defaultValue:
                      'Creates a draft application. The enquiry contact becomes the guardian. Enter the child and the intended campus, session, and grade.',
                  })}
                />
                <Form form={convertForm} layout="vertical" onFinish={(values) => convert.mutate(values)}>
                  <Form.Item name="first_name" label={t('firstName')} rules={[{ required: true }]}>
                    <Input maxLength={100} />
                  </Form.Item>
                  <Form.Item name="last_name" label={t('lastName')}>
                    <Input maxLength={100} />
                  </Form.Item>
                  <Form.Item name="date_of_birth" label={t('dateOfBirth', { defaultValue: 'Date of birth' })}>
                    <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                  </Form.Item>
                  <Form.Item name="campus_id" label={t('campus')} rules={[{ required: true }]}>
                    <EducationCampusSelect />
                  </Form.Item>
                  <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
                    <EducationSessionSelect />
                  </Form.Item>
                  <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}>
                    <EducationGradeSelect />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={convert.isPending}>
                    {t('convertEnquiry', { defaultValue: 'Convert to application' })}
                  </Button>
                </Form>
              </Card>
            ) : null}
          </>
        )}
      </Space>
    </EducationPageChrome>
  );
}
